import type {
  XLMCellAddress,
  XLMGuidanceRecord,
  XLMModel,
  XLMNode,
  XLMValue,
} from "../api/e2e";
import {
  modelReferenceFromCellAddress,
  type ModelReference,
} from "./modelReferences";
import { xlmFigureColorContext, xlmFigureColorRole } from "./xlmFigureColor";
import { recalculateXLMFormulas } from "./xlmFormulaEvaluation";

export interface XLMAssumptionsReviewRow {
  id: string;
  item: string;
  year: number;
  quarter: number | null;
  period: string;
  unit: string | null;
  assumption: number | null;
  actual: number | null;
  average: number | null;
  segment: string | null;
  impact: number | null;
  impactRate: number | null;
  reason: string;
  document: string | null;
  reference: ModelReference | null;
}

export interface XLMGuidanceReviewRow {
  id: string;
  item: string;
  period: string;
  unit: string | null;
  providerType: XLMGuidanceRecord["provider_type"];
  provider: string;
  isaraValue: number | null;
  providerValue: number;
  relativeDeviation: number | null;
  absoluteDirection: number | null;
  explanation: string;
  document: string | null;
  reference: ModelReference | null;
}

export type XLMReviewTab = "assumptions" | "guidance";
export const XLM_REVIEW_RECORD_PARAM = "review-ref";

export interface XLMCellReviewLink {
  tab: XLMReviewTab;
  recordId: string;
  label: string;
  detail: string;
}

interface XLMNodeContext {
  node: XLMNode;
  parent: XLMNode | null;
}

const CURRENCY_SCALE_TO_MM: Readonly<Record<string, number>> = {
  currency_b: 1_000,
  currency_mm: 1,
  currency_k: 0.001,
  currency_unit: 0.000001,
};
const REVENUE_BUILD_SECTION_NAME = "revenue build";

function nodeContext(
  model: XLMModel,
  identifier: string,
): XLMNodeContext | null {
  for (const node of model.nodes) {
    if (node.identifier === identifier) return { node, parent: null };
    const item = node.breakdown?.items.find(
      (candidate) => candidate.identifier === identifier,
    );
    if (item) return { node: item, parent: node };
  }
  return null;
}

function words(value: string): string {
  return ` ${value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, " ")} `;
}

function currencyScaleToMM(node: XLMNode): number | null {
  const unit = node.unit?.trim().toLocaleLowerCase() ?? "";
  return CURRENCY_SCALE_TO_MM[unit] ?? null;
}

function isRevenueOutput(node: XLMNode): boolean {
  return (
    words(node.name).includes(" revenue ") && currencyScaleToMM(node) !== null
  );
}

function segmentRevenueNodes(
  model: XLMModel,
  segment: XLMNode,
  address: XLMCellAddress,
): XLMNode[] {
  const segmentIndex = model.nodes.findIndex(
    (node) => node.identifier === segment.identifier,
  );
  const block: XLMNode[] = [];
  if (segmentIndex >= 0) {
    for (const node of model.nodes.slice(segmentIndex + 1)) {
      if (node.type === "section_title" || node.type === "title") break;
      block.push(node);
    }
  }
  let candidates = block.filter(
    (node) => isRevenueOutput(node) && hasFormulaForAddress(node, address),
  );
  if (candidates.length === 0) {
    const segmentWords = words(segment.name)
      .trim()
      .split(" ")
      .filter(
        (word) =>
          !" build driver drivers model revenue section segment ".includes(
            ` ${word} `,
          ),
      );
    if (segmentWords.length > 0) {
      candidates = model.nodes.filter((node) => {
        if (!isRevenueOutput(node) || !hasFormulaForAddress(node, address)) {
          return false;
        }
        const candidateWords = words(node.name);
        return segmentWords.every((word) =>
          candidateWords.includes(` ${word} `),
        );
      });
    }
  }
  const preferred = candidates.filter(
    (node) => node.type === "total" || words(node.name).includes(" total "),
  );
  return preferred.length > 0 ? preferred : candidates;
}

function valuesForAddress(
  context: XLMNodeContext,
  address: XLMCellAddress,
): XLMValue[] {
  if (address.collection === "yearly") {
    return context.node.yearly_values ?? [];
  }
  if (address.collection === "quarterly") {
    return context.node.quarterly_values ?? [];
  }
  if (address.collection === "half_year") {
    return context.node.half_year_values ?? [];
  }
  const breakdown = context.node.breakdown;
  if (address.collection === "yearly_reported_total") {
    return breakdown?.yearly_reported_totals ?? [];
  }
  if (address.collection === "quarterly_reported_total") {
    return breakdown?.quarterly_reported_totals ?? [];
  }
  return breakdown?.half_year_reported_totals ?? [];
}

function valueMatchesAddress(
  value: XLMValue,
  address: XLMCellAddress,
): boolean {
  if (value.year !== address.year) return false;
  if (address.collection.startsWith("quarterly")) {
    return value.quarter === address.index;
  }
  if (address.collection.startsWith("half_year")) {
    return value.half === address.index;
  }
  return true;
}

function sameCellAddress(left: XLMCellAddress, right: XLMCellAddress): boolean {
  return (
    left.identifier === right.identifier &&
    left.collection === right.collection &&
    left.year === right.year &&
    (left.index ?? null) === (right.index ?? null)
  );
}

interface XLMHardcodedForecastCell {
  node: XLMNode;
  segment: XLMNode | null;
  address: XLMCellAddress;
  value: XLMValue;
}

function assumptionRowId(address: XLMCellAddress): string {
  return `${address.identifier}:${address.collection}:${address.year}:${address.index ?? 0}`;
}

function cellAddress(
  identifier: string,
  collection: XLMCellAddress["collection"],
  value: XLMValue,
): XLMCellAddress {
  if (collection === "quarterly") {
    return {
      identifier,
      collection,
      year: value.year,
      index: value.quarter ?? null,
    };
  }
  if (collection === "half_year") {
    return {
      identifier,
      collection,
      year: value.year,
      index: value.half ?? null,
    };
  }
  return { identifier, collection, year: value.year, index: null };
}

function isForecastCell(model: XLMModel, address: XLMCellAddress): boolean {
  const cutoff = model.last_historical_period;
  if (!cutoff) return false;
  if (address.collection === "yearly") {
    return !(
      address.year < cutoff.year ||
      (address.year === cutoff.year && cutoff.quarter === 4)
    );
  }
  const quarter =
    address.collection === "quarterly"
      ? (address.index ?? 0)
      : (address.index ?? 0) * 2;
  return (
    address.year > cutoff.year ||
    (address.year === cutoff.year && quarter > cutoff.quarter)
  );
}

function isRenderedFySum(
  model: XLMModel,
  node: XLMNode,
  value: XLMValue,
): boolean {
  const unit = node.unit?.trim().toLocaleLowerCase();
  if (unit === "percent" || unit === "%") return false;
  if (value.formula != null || typeof value.value !== "number") return false;

  const useQuarters = model.quarters.length > 0;
  const renderedIndexes = Array.from(
    new Set(
      useQuarters
        ? model.quarters
            .filter((period) => period.year === value.year)
            .map((period) => period.quarter)
        : model.half_years
            .filter((period) => period.year === value.year)
            .map((period) => period.half),
    ),
  ).sort((left, right) => left - right);
  if (renderedIndexes.length < 2) return false;

  const sourceValues = useQuarters
    ? (node.quarterly_values ?? [])
    : (node.half_year_values ?? []);
  const interimValues: number[] = [];
  for (const index of renderedIndexes) {
    const interim = sourceValues.find(
      (candidate) =>
        candidate.year === value.year &&
        (useQuarters ? candidate.quarter : candidate.half) === index,
    );
    if (interim?.formula != null || typeof interim?.value !== "number") {
      return false;
    }
    interimValues.push(interim.value);
  }
  if (interimValues.every((interim) => Math.abs(interim) <= 1e-9)) {
    return false;
  }
  const total = interimValues.reduce((sum, interim) => sum + interim, 0);
  return Math.abs(value.value - total) <= 1e-6 + 1e-3 * Math.abs(value.value);
}

/**
 * Every qualifying Revenue Build forecast input, in rendered cell order.
 */
function hardcodedForecastCells(model: XLMModel): XLMHardcodedForecastCell[] {
  const cells: XLMHardcodedForecastCell[] = [];
  const figureColorContext = xlmFigureColorContext(model);
  let segment: XLMNode | null = null;
  let inRevenueBuild = false;
  const collect = (node: XLMNode): void => {
    const groups: [XLMCellAddress["collection"], XLMValue[]][] = [
      ["yearly", node.yearly_values ?? []],
      ["half_year", node.half_year_values ?? []],
      ["quarterly", node.quarterly_values ?? []],
    ];
    for (const [collection, values] of groups) {
      for (const value of values) {
        const address = cellAddress(node.identifier, collection, value);
        const figureColorRole = xlmFigureColorRole(value, {
          forecast: isForecastCell(model, address),
          formulaOnlyPercentRow: false,
          model: figureColorContext,
        });
        if (
          figureColorRole !== "forecast-input" ||
          typeof value.value !== "number" ||
          value.value === 0 ||
          !value.comment?.trim() ||
          (collection === "yearly" && isRenderedFySum(model, node, value))
        ) {
          continue;
        }
        cells.push({ node, segment, address, value });
      }
    }
  };
  for (const node of model.nodes) {
    if (node.type === "section_title") {
      inRevenueBuild =
        node.name.trim().toLowerCase() === REVENUE_BUILD_SECTION_NAME;
      segment = inRevenueBuild ? node : null;
    } else if (inRevenueBuild && node.type === "title") {
      segment = node;
    }
    if (!inRevenueBuild) continue;
    collect(node);
    for (const item of node.breakdown?.items ?? []) collect(item);
  }
  return cells;
}

export function xlmCellReviewLinks(
  model: XLMModel,
  address: XLMCellAddress,
): XLMCellReviewLink[] {
  const assumptions = hardcodedForecastCells(model)
    .filter((cell) => sameCellAddress(cell.address, address))
    .map((cell) => ({
      tab: "assumptions" as const,
      recordId: assumptionRowId(cell.address),
      label: "Assumptions Review",
      detail: "Hardcoded forecast assumption",
    }));
  const guidance = model.guidance
    .filter((record) => sameCellAddress(record.target_ref, address))
    .map((record) => ({
      tab: "guidance" as const,
      recordId: record.record_id,
      label: "Guidance",
      detail: `${record.provider_type} · ${record.provider}`,
    }));
  return [...assumptions, ...guidance];
}

function comparableSubperiodMatches(
  value: XLMValue,
  address: XLMCellAddress,
): boolean {
  if (address.collection.startsWith("quarterly")) {
    return value.quarter === address.index;
  }
  if (address.collection.startsWith("half_year")) {
    return value.half === address.index;
  }
  return true;
}

function resolvedNumber(value: XLMValue | undefined): number | null {
  if (typeof value?.computed_value === "number") {
    return value.computed_value;
  }
  return typeof value?.value === "number" ? value.value : null;
}

function valueForAddress(
  context: XLMNodeContext | null,
  address: XLMCellAddress,
): XLMValue | undefined {
  if (!context) return undefined;
  return valuesForAddress(context, address).find((value) =>
    valueMatchesAddress(value, address),
  );
}

function hasFormulaForAddress(node: XLMNode, address: XLMCellAddress): boolean {
  return Boolean(valueForAddress({ node, parent: null }, address)?.formula);
}

interface XLMSegmentSensitivity {
  amount: number;
  rate: number | null;
}

/**
 * Best-effort +1pp effect of one hardcoded forecast cell on segment revenue.
 *
 * The working model is shocked, recalculated from the changed cell, read, then
 * restored, so one clone serves every assumption row.
 */
function segmentRevenueSensitivity(
  working: XLMModel,
  segment: XLMNode | null,
  address: XLMCellAddress,
): XLMSegmentSensitivity | null {
  if (!segment) return null;
  const target = nodeContext(working, address.identifier);
  const targetValue = target ? valueForAddress(target, address) : undefined;
  if (!target || typeof targetValue?.value !== "number") return null;

  const revenueNodes = segmentRevenueNodes(working, segment, address);
  if (revenueNodes.length === 0) return null;

  const revenueTotal = (): number | null => {
    let total = 0;
    for (const revenueNode of revenueNodes) {
      const scale = currencyScaleToMM(revenueNode);
      const value = resolvedNumber(
        valueForAddress(nodeContext(working, revenueNode.identifier), address),
      );
      if (scale === null || value === null) return null;
      total += value * scale;
    }
    return total;
  };

  const baselineValue = targetValue.value;
  const baselineTotal = revenueTotal();
  if (baselineTotal === null) return null;

  const targetUnit = nodeUnit(target)?.trim().toLocaleLowerCase();
  const shock =
    targetUnit === "percent" || targetUnit === "%"
      ? 0.01
      : Math.max(Math.abs(baselineValue) * 0.01, 0.01);
  targetValue.value = baselineValue + shock;
  recalculateXLMFormulas(working, address);
  const shockedTotal = revenueTotal();
  targetValue.value = baselineValue;
  recalculateXLMFormulas(working, address);
  if (shockedTotal === null) return null;

  const amount = shockedTotal - baselineTotal;
  const rate =
    baselineTotal === 0
      ? amount === 0
        ? 0
        : null
      : amount / Math.abs(baselineTotal);
  if (!Number.isFinite(amount) || (rate !== null && !Number.isFinite(rate))) {
    return null;
  }
  return { amount, rate };
}

function isHistorical(
  model: XLMModel,
  address: XLMCellAddress,
  value: XLMValue,
): boolean {
  const cutoff = model.last_historical_period;
  if (!cutoff) return true;
  const quarter = address.collection.startsWith("yearly")
    ? 4
    : address.collection.startsWith("quarterly")
      ? (value.quarter ?? 0)
      : (value.half ?? 0) * 2;
  return (
    value.year < cutoff.year ||
    (value.year === cutoff.year && quarter <= cutoff.quarter)
  );
}

function historicalValues(
  model: XLMModel,
  address: XLMCellAddress,
  values: XLMValue[],
): number[] {
  return values
    .filter(
      (value) =>
        value.year < address.year &&
        comparableSubperiodMatches(value, address) &&
        isHistorical(model, address, value),
    )
    .sort((left, right) => right.year - left.year)
    .slice(0, 3)
    .map(resolvedNumber)
    .filter((value): value is number => value !== null);
}

function nodeUnit(context: XLMNodeContext): string | null {
  if (context.parent) {
    return (
      context.node.unit ??
      context.parent.breakdown?.unit ??
      context.parent.unit ??
      null
    );
  }
  return context.node.breakdown?.unit ?? context.node.unit ?? null;
}

function periodLabel(address: XLMCellAddress): string {
  if (address.collection.startsWith("quarterly")) {
    return `Q${address.index} FY${address.year}`;
  }
  if (address.collection.startsWith("half_year")) {
    return `H${address.index} FY${address.year}`;
  }
  return `FY${address.year}`;
}

function primaryDocumentId(value: XLMValue): string | null {
  return value.document_references?.[0]?.document_id ?? null;
}

function nodeOrder(model: XLMModel): ReadonlyMap<string, number> {
  const order = new Map<string, number>();
  let index = 0;
  for (const node of model.nodes) {
    order.set(node.identifier, index);
    index += 1;
    for (const item of node.breakdown?.items ?? []) {
      order.set(item.identifier, index);
      index += 1;
    }
  }
  return order;
}

export function xlmAssumptionsReviewRows(
  model: XLMModel,
): XLMAssumptionsReviewRow[] {
  const cells = hardcodedForecastCells(model);
  if (cells.length === 0) return [];
  // One working clone serves every row: each sensitivity shocks its cell,
  // recalculates from it, reads the segment total, then restores the cell.
  const working = structuredClone(model);
  recalculateXLMFormulas(working);
  return cells.map((cell) => {
    const context = nodeContext(model, cell.address.identifier);
    const values = context ? valuesForAddress(context, cell.address) : [];
    const history = historicalValues(model, cell.address, values);
    const average =
      history.length === 3
        ? history.reduce((sum, value) => sum + value, 0) / history.length
        : null;
    const workingSegment = cell.segment
      ? (nodeContext(working, cell.segment.identifier)?.node ?? null)
      : null;
    const sensitivity = segmentRevenueSensitivity(
      working,
      workingSegment,
      cell.address,
    );
    return {
      id: assumptionRowId(cell.address),
      item: cell.node.name,
      year: cell.address.year,
      quarter:
        cell.address.collection === "quarterly" ? cell.address.index : null,
      period: periodLabel(cell.address),
      unit: context ? nodeUnit(context) : null,
      assumption: resolvedNumber(cell.value),
      actual: history[0] ?? null,
      average,
      segment: cell.segment?.name ?? null,
      impact: sensitivity?.amount ?? null,
      impactRate: sensitivity?.rate ?? null,
      reason: cell.value.comment ?? "",
      document: primaryDocumentId(cell.value),
      reference: modelReferenceFromCellAddress(cell.address),
    };
  });
}

const GUIDANCE_PROVIDER_ORDER: Readonly<
  Record<XLMGuidanceRecord["provider_type"], number>
> = {
  Guidance: 0,
  Consensus: 1,
  "Independent forecast": 2,
};

export function xlmGuidanceReviewRows(model: XLMModel): XLMGuidanceReviewRow[] {
  const order = nodeOrder(model);
  return [...model.guidance]
    .sort((left, right) => {
      const leftOrder =
        order.get(left.target_ref.identifier) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder =
        order.get(right.target_ref.identifier) ?? Number.MAX_SAFE_INTEGER;
      return (
        leftOrder - rightOrder ||
        left.target_ref.year - right.target_ref.year ||
        (left.target_ref.index ?? 0) - (right.target_ref.index ?? 0) ||
        left.target_ref.collection.localeCompare(right.target_ref.collection) ||
        GUIDANCE_PROVIDER_ORDER[left.provider_type] -
          GUIDANCE_PROVIDER_ORDER[right.provider_type] ||
        left.provider.localeCompare(right.provider) ||
        left.record_id.localeCompare(right.record_id)
      );
    })
    .map((record) => {
      const context = nodeContext(model, record.target_ref.identifier);
      const isaraValue = resolvedNumber(
        valueForAddress(context, record.target_ref),
      );
      const absoluteDirection =
        isaraValue === null ? null : isaraValue - record.provider_value;
      return {
        id: record.record_id,
        item: context?.node.name ?? record.target_ref.identifier,
        period: periodLabel(record.target_ref),
        unit: context ? nodeUnit(context) : null,
        providerType: record.provider_type,
        provider: record.provider,
        isaraValue,
        providerValue: record.provider_value,
        relativeDeviation:
          absoluteDirection === null || record.provider_value === 0
            ? null
            : absoluteDirection / Math.abs(record.provider_value),
        absoluteDirection,
        explanation: record.explanation,
        document: record.evidence[0]?.document_id ?? null,
        reference: modelReferenceFromCellAddress(record.target_ref),
      };
    });
}
